import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const getActiveCartKey = () => {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  return user?.email ? `cart_${user.email}` : "cart_guest";
};

const Cart = () => {
  const [cart, setCart] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const cartKey = getActiveCartKey();
    const savedCart = JSON.parse(localStorage.getItem(cartKey)) || [];
    setCart(savedCart);
  }, []);

  const getCartKey = (item, index) =>
    `${item.id || item.name}-${item.variant_id || item.selected_price || item.price}-${index}`;

  const isSameCartItem = (a, b) =>
    String(a.id || a.name) === String(b.id || b.name) &&
    String(a.variant_id || a.selected_price || a.price) ===
      String(b.variant_id || b.selected_price || b.price);

  const saveCart = (updatedCart) => {
    const cartKey = getActiveCartKey();

    setCart(updatedCart);
    localStorage.setItem(cartKey, JSON.stringify(updatedCart));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const removeFromCart = (itemToRemove) => {
    const updatedCart = cart.filter(
      (item) => !isSameCartItem(item, itemToRemove)
    );
    saveCart(updatedCart);
  };

  const updateQuantity = (itemToUpdate, type) => {
    const updatedCart = cart.map((item) => {
      if (isSameCartItem(item, itemToUpdate)) {
        const currentQty = Number(item.quantity || 1);
        const newQty = type === "inc" ? currentQty + 1 : currentQty - 1;

        return { ...item, quantity: newQty > 0 ? newQty : 1 };
      }

      return item;
    });

    saveCart(updatedCart);
  };

  const totalPrice = cart.reduce(
    (total, item) =>
      total + Number(item.price || 0) * Number(item.quantity || 1),
    0
  );

  const totalItems = cart.reduce(
    (total, item) => total + Number(item.quantity || 1),
    0
  );

  const deliveryCharge = totalPrice >= 499 || totalPrice === 0 ? 0 : 49;
  const grandTotal = totalPrice + deliveryCharge;

  const handleCheckout = () => {
    const cartKey = getActiveCartKey();
    const latestCart = JSON.parse(localStorage.getItem(cartKey)) || [];

    if (!latestCart || latestCart.length === 0) {
      alert("Cart is empty");
      return;
    }

    const latestTotalPrice = latestCart.reduce(
      (total, item) =>
        total + Number(item.price || 0) * Number(item.quantity || 1),
      0
    );

    const latestTotalItems = latestCart.reduce(
      (total, item) => total + Number(item.quantity || 1),
      0
    );

    const latestDeliveryCharge =
      latestTotalPrice >= 499 || latestTotalPrice === 0 ? 0 : 49;

    const latestGrandTotal = latestTotalPrice + latestDeliveryCharge;

    localStorage.setItem(
      "checkoutSummary",
      JSON.stringify({
        cart: latestCart,
        totalItems: latestTotalItems,
        totalPrice: latestTotalPrice,
        deliveryCharge: latestDeliveryCharge,
        grandTotal: latestGrandTotal,
      })
    );

    localStorage.setItem("checkoutCartKey", cartKey);

    navigate("/checkout");
  };

  return (
    <>
      <style>{`
        @media (max-width: 900px) {
          .cart-main-grid {
            grid-template-columns: 1fr !important;
          }

          .cart-summary {
            position: static !important;
          }
        }

        @media (max-width: 600px) {
          .cart-page {
            padding: 14px 10px !important;
          }

          .cart-hero {
            padding: 24px 18px !important;
            border-radius: 22px !important;
          }

          .cart-title {
            font-size: 27px !important;
          }

          .cart-subtitle {
            font-size: 14px !important;
          }

          .cart-steps {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 10px !important;
            margin-bottom: 18px !important;
          }

          .cart-step {
            min-height: 118px !important;
            padding: 12px !important;
            border-radius: 18px !important;
          }

          .cart-step-icon {
            font-size: 23px !important;
            margin-bottom: 8px !important;
          }

          .cart-step-title {
            font-size: 13px !important;
            line-height: 1.25 !important;
          }

          .cart-step-text {
            font-size: 11px !important;
            line-height: 1.35 !important;
          }

          .cart-card,
          .cart-summary,
          .cart-empty {
            padding: 16px !important;
            border-radius: 20px !important;
          }

          .cart-item {
            grid-template-columns: 70px 1fr !important;
            gap: 12px !important;
            padding: 12px !important;
          }

          .cart-img {
            width: 70px !important;
            height: 70px !important;
          }

          .cart-product-title {
            font-size: 15px !important;
          }

          .cart-product-price {
            font-size: 15px !important;
          }

          .cart-price-action {
            grid-column: 1 / -1 !important;
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            text-align: left !important;
          }

          .cart-price-action h3 {
            margin: 0 !important;
            font-size: 16px !important;
          }

          .cart-info-box {
            font-size: 13px !important;
          }
        }

        @media (max-width: 380px) {
          .cart-steps {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <div style={pageStyle} className="cart-page">
        <div style={containerStyle}>
          <div style={heroStyle} className="cart-hero">
            <h1 style={heroTitleStyle} className="cart-title">
              Shopping Cart
            </h1>
            <p style={heroTextStyle} className="cart-subtitle">
              Cart → Checkout → Payment → Tracking
            </p>
          </div>

          <div style={stepsStyle} className="cart-steps">
            {[
              ["🛒", "Cart", "Check your products before checkout"],
              ["💳", "Checkout & Payment", "Add address & complete secure payment"],
              ["🚚📍", "Delivery & Track", "Track your JyotirVani order live"],
            ].map((step, index) => (
              <div
                key={index}
                className="cart-step"
                style={{
                  ...stepCardStyle,
                  border:
                    index === 0 ? "2px solid #7c3aed" : "1px solid #e5e7eb",
                }}
              >
                <div
                  className="cart-step-icon"
                  style={{ fontSize: "28px", marginBottom: "8px" }}
                >
                  {step[0]}
                </div>

                <h3 style={stepTitleStyle} className="cart-step-title">
                  {step[1]}
                </h3>

                <p style={stepTextStyle} className="cart-step-text">
                  {step[2]}
                </p>
              </div>
            ))}
          </div>

          {cart.length === 0 ? (
            <div style={emptyCardStyle} className="cart-empty">
              <div style={{ fontSize: "70px" }}>🛒</div>
              <h2>Your Cart is Empty</h2>
              <p style={{ color: "#6b7280" }}>
                Add astrology products before checkout.
              </p>

              <button
                onClick={() => navigate("/ShopCategoryCard")}
                style={primaryButtonStyle}
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div style={mainGridStyle} className="cart-main-grid">
              <div style={cartCardStyle} className="cart-card">
                <h2 style={{ marginTop: 0 }}>Cart Items ({totalItems})</h2>

                {cart.map((item, index) => (
                  <div
                    key={getCartKey(item, index)}
                    style={cartItemStyle}
                    className="cart-item"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      style={productImageStyle}
                      className="cart-img"
                    />

                    <div>
                      <h3 style={productTitleStyle} className="cart-product-title">
                        {item.name}
                      </h3>

                      {(item.size || item.unit) && (
                        <p style={cartMetaStyle}>
                          {item.size} {item.unit}
                        </p>
                      )}

                      <p style={productPriceStyle} className="cart-product-price">
                        ₹{item.price}
                      </p>

                      <div style={qtyWrapStyle}>
                        <button
                          onClick={() => updateQuantity(item, "dec")}
                          style={qtyBtnStyle}
                        >
                          −
                        </button>

                        <strong>{item.quantity || 1}</strong>

                        <button
                          onClick={() => updateQuantity(item, "inc")}
                          style={qtyBtnStyle}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div
                      style={{ textAlign: "right" }}
                      className="cart-price-action"
                    >
                      <h3 style={{ margin: "0 0 12px" }}>
                        ₹{Number(item.price || 0) * Number(item.quantity || 1)}
                      </h3>

                      <button
                        onClick={() => removeFromCart(item)}
                        style={removeButtonStyle}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div style={summaryCardStyle} className="cart-summary">
                <h2 style={{ marginTop: 0 }}>Order Summary</h2>

                <SummaryRow
                  label={`Items (${totalItems})`}
                  value={`₹${totalPrice}`}
                />

                <SummaryRow
                  label="Delivery"
                  value={deliveryCharge === 0 ? "Free" : `₹${deliveryCharge}`}
                />

                <div style={totalRowStyle}>
                  <span>Total</span>
                  <span style={{ color: "#7c3aed" }}>₹{grandTotal}</span>
                </div>

                <button onClick={handleCheckout} style={payButtonStyle}>
                  Continue To Checkout
                </button>

                <button
                  onClick={() => navigate("/ShopCategoryCard")}
                  style={outlineButtonStyle}
                >
                  Continue Shopping
                </button>

                <div style={supportBoxStyle} className="cart-info-box">
                  <h3 style={supportTitleStyle}>Need Help?</h3>

                  <a
                    href="mailto:support@jyotirvani.com"
                    style={supportMailStyle}
                  >
                    📧 support@jyotirvani.com
                  </a>

                  <a href="tel:+919616339090" style={supportPhoneStyle}>
                    📞 +91 96163 39090
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const SummaryRow = ({ label, value }) => (
  <div style={summaryRowStyle}>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const pageStyle = {
  minHeight: "100vh",
  background:
    "radial-gradient(circle at top left, #fff7ed 0, #fff 35%, #f5f3ff 100%)",
  padding: "30px 16px",
  fontFamily: "Arial, sans-serif",
};

const containerStyle = {
  maxWidth: "1200px",
  margin: "0 auto",
};

const heroStyle = {
  background: "linear-gradient(135deg, #4c1d95, #7c3aed, #f97316)",
  borderRadius: "28px",
  padding: "34px 24px",
  color: "#fff",
  marginBottom: "25px",
  boxShadow: "0 22px 45px rgba(76,29,149,0.25)",
};

const heroTitleStyle = {
  margin: 0,
  fontSize: "34px",
  fontWeight: "800",
};

const heroTextStyle = {
  margin: "10px 0 0",
  opacity: 0.9,
};

const stepsStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "14px",
  marginBottom: "25px",
};

const stepCardStyle = {
  background: "#fff",
  borderRadius: "18px",
  padding: "18px",
  boxShadow: "0 12px 30px rgba(15,23,42,0.08)",
};

const stepTitleStyle = {
  margin: 0,
  color: "#111827",
  fontSize: "17px",
};

const stepTextStyle = {
  margin: "6px 0 0",
  color: "#6b7280",
  fontSize: "13px",
};

const emptyCardStyle = {
  background: "#fff",
  borderRadius: "24px",
  padding: "60px 25px",
  textAlign: "center",
  boxShadow: "0 18px 45px rgba(15,23,42,0.08)",
};

const mainGridStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) 360px",
  gap: "24px",
  alignItems: "start",
};

const cartCardStyle = {
  background: "#fff",
  borderRadius: "24px",
  padding: "24px",
  boxShadow: "0 18px 45px rgba(15,23,42,0.08)",
};

const cartItemStyle = {
  display: "grid",
  gridTemplateColumns: "90px 1fr auto",
  gap: "18px",
  alignItems: "center",
  background: "#f9fafb",
  border: "1px solid #eee",
  borderRadius: "18px",
  padding: "16px",
  marginBottom: "15px",
};

const productImageStyle = {
  width: "90px",
  height: "90px",
  objectFit: "cover",
  borderRadius: "14px",
};

const productTitleStyle = {
  margin: "0 0 8px",
  fontSize: "18px",
};

const cartMetaStyle = {
  margin: "0 0 8px",
  color: "#6d28d9",
  fontWeight: "800",
  fontSize: "13px",
};

const productPriceStyle = {
  margin: "0 0 10px",
  color: "#7c3aed",
  fontWeight: "800",
  fontSize: "18px",
};

const qtyWrapStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const qtyBtnStyle = {
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  border: "1px solid #7c3aed",
  background: "#fff",
  color: "#7c3aed",
  fontWeight: "800",
  cursor: "pointer",
};

const removeButtonStyle = {
  border: "none",
  borderRadius: "999px",
  padding: "9px 15px",
  background: "#fee2e2",
  color: "#dc2626",
  fontWeight: "700",
  cursor: "pointer",
};

const summaryCardStyle = {
  background: "#fff",
  borderRadius: "24px",
  padding: "24px",
  boxShadow: "0 18px 45px rgba(15,23,42,0.08)",
  position: "sticky",
  top: "20px",
};

const summaryRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "12px",
  color: "#4b5563",
  fontSize: "15px",
};

const totalRowStyle = {
  borderTop: "1px dashed #d1d5db",
  marginTop: "15px",
  paddingTop: "15px",
  display: "flex",
  justifyContent: "space-between",
  fontSize: "20px",
  fontWeight: "800",
};

const payButtonStyle = {
  width: "100%",
  marginTop: "22px",
  border: "none",
  borderRadius: "999px",
  padding: "15px",
  background: "linear-gradient(135deg, #16a34a, #22c55e)",
  color: "#fff",
  fontWeight: "800",
  fontSize: "16px",
  cursor: "pointer",
  boxShadow: "0 12px 25px rgba(34,197,94,0.25)",
};

const outlineButtonStyle = {
  width: "100%",
  marginTop: "12px",
  border: "2px solid #7c3aed",
  borderRadius: "999px",
  padding: "13px",
  background: "#fff",
  color: "#7c3aed",
  fontWeight: "800",
  cursor: "pointer",
};

const primaryButtonStyle = {
  marginTop: "18px",
  border: "none",
  borderRadius: "999px",
  padding: "14px 28px",
  background: "linear-gradient(135deg, #7c3aed, #f97316)",
  color: "#fff",
  fontWeight: "700",
  cursor: "pointer",
};

const supportBoxStyle = {
  marginTop: "18px",
  background: "linear-gradient(135deg, #f5f3ff, #fff7ed)",
  border: "1px solid #ede9fe",
  borderRadius: "18px",
  padding: "16px",
  boxShadow: "0 10px 24px rgba(124,58,237,0.08)",
};

const supportTitleStyle = {
  margin: "0 0 12px",
  color: "#4c1d95",
  fontSize: "17px",
  fontWeight: "900",
};

const supportMailStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  textDecoration: "none",
  background: "#ffffff",
  color: "#6d28d9",
  padding: "12px 14px",
  borderRadius: "14px",
  fontWeight: "800",
  fontSize: "14px",
  marginBottom: "10px",
  border: "1px solid #ede9fe",
};

const supportPhoneStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  textDecoration: "none",
  background: "#ecfdf5",
  color: "#16a34a",
  padding: "12px 14px",
  borderRadius: "14px",
  fontWeight: "800",
  fontSize: "14px",
  border: "1px solid #bbf7d0",
};

export default Cart;